import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { useAuth } from '../../hooks/useAuth'
import { fetchPendingApprovals } from '../../api/brain'

interface NavItem {
  label: string
  href: string
}

interface HeaderProps {
  navItems?: NavItem[]
}

const defaultNavItems: NavItem[] = [
  { label: 'Dashboard', href: '/' },
  { label: 'Brains', href: '/brains' },
  { label: 'Inbox', href: '/inbox' },
  { label: 'Work Orders', href: '/work-orders' },
  { label: 'Round Tables', href: '/round-tables' },
  { label: 'Narratives', href: '/narratives' },
]

export function Header({ navItems = defaultNavItems }: HeaderProps) {
  const location = useLocation()
  const navigate = useNavigate()
  const { user, signOut } = useAuth()
  const pendingApprovalsQuery = useQuery({
    queryKey: ['brain', 'approvals', 'pending-header-count'],
    queryFn: fetchPendingApprovals,
    refetchInterval: 30_000,
    staleTime: 15_000,
  })
  const pendingApprovalCount = pendingApprovalsQuery.data?.length || 0

  const handleSignOut = async () => {
    await signOut()
    navigate('/login')
  }

  return (
    <header className="border-b border-swiss-gray-200 py-4 sm:py-6 lg:py-8">
      <div className="mx-auto flex max-w-[1440px] flex-col gap-4 px-4 sm:px-8 lg:flex-row lg:items-center lg:justify-between lg:px-12">
        <Link to="/" className="flex items-center gap-3">
          <div className="w-6 h-6 bg-swiss-black flex items-center justify-center">
            <span className="text-swiss-white text-xs font-bold">D</span>
          </div>
          <span className="text-[16px] font-semibold tracking-tight">
            Derivative Admin
          </span>
        </Link>

        <div className="flex flex-col gap-3 lg:items-end">
          <nav className="flex flex-wrap gap-x-4 gap-y-2 lg:gap-8">
            {navItems.map((item) => {
              const isActive = location.pathname === item.href
              return (
                <Link
                  key={item.href}
                  to={item.href}
                  className={
                    isActive
                      ? 'text-[13px] text-swiss-black font-medium cursor-pointer'
                      : 'text-[13px] text-swiss-gray-500 cursor-pointer transition-colors duration-150 hover:text-swiss-black'
                  }
                >
                  <span className="inline-flex items-center gap-2">
                    <span>{item.label}</span>
                    {item.href === '/work-orders' && pendingApprovalCount > 0 && (
                      <span className="inline-flex min-w-[20px] items-center justify-center border border-swiss-black px-1.5 py-0.5 text-[11px] font-medium text-swiss-black">
                        {pendingApprovalCount}
                      </span>
                    )}
                  </span>
                </Link>
              )
            })}
          </nav>

          <div className="flex items-center justify-between gap-3 lg:justify-end">
            {pendingApprovalCount > 0 && (
              <Link
                to="/work-orders"
                className="text-[12px] font-medium uppercase tracking-widest text-swiss-gray-500 transition-colors duration-150 hover:text-swiss-black"
              >
                {pendingApprovalCount} pending
              </Link>
            )}
            {user && (
              <button
                onClick={handleSignOut}
                className="text-[13px] text-swiss-gray-500 cursor-pointer transition-colors duration-150 hover:text-swiss-black"
              >
                Sign Out
              </button>
            )}
          </div>
        </div>
      </div>
    </header>
  )
}
